import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import ErrorMessage from '@components/Common/ErrorMessage';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { premiumEmployers } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ActionIconButton from '@components/Common/ActionIconButton';
import { humanizeEnum } from '@helpers/labels';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import JobAssignmentList from './JobAssignmentList';
import './index.scss';

const rowsPerPage = 100;

interface PremiumEmployer {
  _id: string;
  premiumTier?: string | null;
  companyId?: string | null;
  company?: { companyName?: string } | null;
  personalDetails?: {
    fullName?: string;
    email?: string;
    phone?: string;
  } | null;
}

const HR: React.FC = () => {
  const [page, setPage] = useState(0);
  const [employers, setEmployers] = useState<PremiumEmployer[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The client whose jobs are being assigned (null = dialog closed).
  const [assignFor, setAssignFor] = useState<PremiumEmployer | null>(null);

  const fetchEmployers = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchGraphQLApiData(premiumEmployers(pageNum, rowsPerPage));
      const data = res?.data?.getAllEmployers;
      setEmployers((data?.content ?? []).filter(Boolean));
      setTotalElements(data?.totalElements ?? 0);
    } catch {
      setError('Failed to load Premium Hiring clients. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployers(page);
  }, [fetchEmployers, page]);

  /**
   * A managed client is an employer *account*, not a business.
   *
   * `isPremium`, `premiumTier` and `assignedHrId` are all fields on Employer, and
   * `HrAssignment.clientId` resolves through the employer repository, so the row's identity is the
   * person. It was labelled with the company alone, which made two rows unreadable when one
   * company has two premium accounts -- `Company.employers` is a list, while each employer holds
   * one company -- and implied the screen acted on a business, which is the assumption behind the
   * job list once asking for a single account's listings and finding almost none.
   */
  const employerName = (e: PremiumEmployer) =>
    e.personalDetails?.fullName || e.personalDetails?.email || 'Unnamed contact';

  const companyName = (e: PremiumEmployer) => e.company?.companyName || '';

  const selectors = ['client', 'contact', 'tier', 'action'];
  const tableHeadRow: Record<string, React.ReactNode> = {
    client: 'Client',
    contact: 'Contact',
    tier: 'Tier',
    action: 'Actions',
  };
  /*
   * The action column used to be unlabelled, and the phone card drops unlabelled columns, so on a
   * phone there was no way to assign jobs at all. It is a card action now (footer, no label), and
   * the tablet table keeps it rather than folding it into the row detail.
   */
  const columns: ColumnsConfig = {
    client: { card: 'title', tablet: true },
    contact: { card: 'subtitle' },
    tier: { card: 'fact', tablet: true },
    action: { card: 'action', tablet: true },
  };
  const tableBodyRow: Record<string, (row: PremiumEmployer) => React.ReactNode> = {
    client: (row) => (
      <Box className="hr-client">
        <Typography className="hr-employer-name">{employerName(row)}</Typography>
        {companyName(row) && <span className="hr-company-name">{companyName(row)}</span>}
      </Box>
    ),
    contact: (row) => (
      <Box className="hr-contact">
        <span>{row.personalDetails?.email || '-'}</span>
        {row.personalDetails?.phone && <span className="hr-phone">{row.personalDetails.phone}</span>}
      </Box>
    ),
    // Stored values arrive as MANAGED beside Scale and Growth; sentence case makes them one list.
    tier: (row) => <Chip label={humanizeEnum(row.premiumTier || 'MANAGED')} />,
    // An icon with its name in the tooltip, as on every other row action: a worded outlined
    // button on each of thirteen rows was the loudest thing on the page.
    action: (row) => (
      <Box className="hr-row-actions">
        <ActionIconButton
          size="small"
          label={`Assign jobs to ${employerName(row)}`}
          icon={<AssignmentIndOutlinedIcon />}
          onClick={() => setAssignFor(row)}
        />
      </Box>
    ),
  };

  return (
    <Box className="hr-container">
      <CRMPageTemplate
        title="HR: Premium Hiring Clients"
        icon={<SupervisorAccountOutlinedIcon />}
        totalCount={totalElements}
      >
        {error ? (
          <ErrorMessage message={error} />
        ) : loading ? (
          <SkeletonLoader columns={selectors.length} />
        ) : (
          <MintlyTable
            rows={employers}
            selectors={selectors}
            columns={columns}
            getRowKey={(row: PremiumEmployer) => row._id}
            tableHeadRow={tableHeadRow}
            tableBodyRow={
              tableBodyRow as Record<string, (row: any, index: number) => React.ReactNode>
            }
            pagination={{
              enabled: true,
              page,
              rowsPerPage,
              totalNumberOfRows: totalElements,
              handleChangePage: (_e, newPage) => setPage(newPage),
            }}
          />
        )}
      </CRMPageTemplate>

      <Dialog open={!!assignFor} onClose={() => setAssignFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle className="hr-dialog-title">
          Assign jobs
          <span className="hr-dialog-company">
            {assignFor
              ? [employerName(assignFor), companyName(assignFor)].filter(Boolean).join(' · ')
              : ''}
          </span>
        </DialogTitle>
        <DialogContent>
          {assignFor && (
            <JobAssignmentList companyId={assignFor.companyId} employerId={assignFor._id} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignFor(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HR;
